import React, { useState, useEffect, useRef } from "react";
import { BiCheckbox } from "react-icons/bi";
import { FaBars, FaTimes } from "react-icons/fa";
import { useNavigate, useLocation } from "react-router-dom";

function MeetTheBoard() {
  const navigate = useNavigate();
  const location = useLocation();
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  const [headerHeight, setHeaderHeight] = useState(0);
  const [loading, setLoading] = useState(true);
  const [headerData, setHeaderData] = useState({
    main_heading: "Meet the Board",
    description: "The directors of the DLA board are elected to their positions by the DLA members at the AGM. The President and Vice President of the DLA board are elected on a five-year term, the rest of the DLA board members are elected on a three-year term."
  });
  const [boardMembers, setBoardMembers] = useState([]);
  const headerRef = useRef(null);

  const greenColor = "#70B444";
  const API_BASE_URL = 'https://backend.dlapakistan.com/api/board';

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        
        try {
          const headerResponse = await fetch(`${API_BASE_URL}/header/active/`);
          if (headerResponse.ok) {
            const data = await headerResponse.json();
            if (data) {
              setHeaderData(data);
            }
          }
        } catch (headerErr) {
          console.log('Header fetch error, using defaults:', headerErr);
        }
        
        try {
          const membersResponse = await fetch(`${API_BASE_URL}/members/active/`);
          if (membersResponse.ok) {
            const members = await membersResponse.json();
            if (members && Array.isArray(members)) {
              const processedMembers = members.map(member => ({
                ...member,
                image_url: getImageUrl(member.image_url),
                subordinates: (member.subordinates || []).map(sub => ({
                  ...sub,
                  image_url: getImageUrl(sub.image_url)
                }))
              }));
              setBoardMembers(processedMembers);
            } else {
              loadFallbackData();
            }
          } else {
            loadFallbackData();
          }
        } catch (memberErr) {
          console.log('Members fetch error, using fallback:', memberErr);
          loadFallbackData();
        }
        
      } catch (err) {
        console.error('Error in fetchData:', err);
        loadFallbackData();
      } finally {
        setLoading(false);
      }
    };

    const loadFallbackData = () => {
      setBoardMembers([
        {
          id: 1,
          name: "Ian Cumberland",
          position: "DLA President",
          image_url: "/dla1.png",
          route: "/board-member/1",
          description: "Experienced leader in dental laboratory industry with over 20 years of experience...",
          subordinates: []
        },
        {
          id: 2,
          name: "Steve Campbell",
          position: "Director of Policy",
          image_url: "/dla2.png",
          route: "/board-member/2",
          description: "Policy expert with years of experience in dental regulations...",
          subordinates: []
        },
        {
          id: 3,
          name: "Ashley Byrne",
          position: "Director for Communications",
          image_url: "/dla3.png",
          route: "/board-member/3",
          description: "Communications specialist leading public relations efforts...",
          subordinates: []
        },
        {
          id: 4,
          name: "Dan Barber",
          position: "Director of Finance",
          image_url: "/dla4.png",
          route: "/board-member/4",
          description: "Financial expert managing budgets and investments...",
          subordinates: []
        }
      ]);
    };

    const getImageUrl = (imageUrl) => {
      if (!imageUrl) return null;
      if (imageUrl.startsWith('http://') || imageUrl.startsWith('https://')) {
        return imageUrl;
      }
      if (imageUrl.startsWith('/media/') || imageUrl.startsWith('/')) {
        return `https://backend.dlapakistan.com${imageUrl}`;
      }
      return imageUrl;
    };

    fetchData();
    
    const updateHeaderHeight = () => {
      const header = document.querySelector('header') || 
                    document.querySelector('.header') ||
                    document.querySelector('#header');
      
      if (header) {
        setHeaderHeight(header.offsetHeight);
      } else {
        if (windowWidth < 640) {
          setHeaderHeight(80);
        } else if (windowWidth < 768) {
          setHeaderHeight(96);
        } else {
          setHeaderHeight(120);
        }
      }
    };

    const handleResize = () => {
      setWindowWidth(window.innerWidth);
      updateHeaderHeight();
      if (window.innerWidth > 1029) {
        setIsSidebarOpen(false);
      }
    };

    updateHeaderHeight();
    window.addEventListener("resize", handleResize);
    
    const observer = new ResizeObserver(updateHeaderHeight);
    const headerElement = headerRef.current || 
                         document.querySelector('header');
    if (headerElement) observer.observe(headerElement);

    return () => {
      window.removeEventListener("resize", handleResize);
      if (headerElement) observer.unobserve(headerElement);
    };
  }, []);

  const isMobileView = windowWidth <= 1029;

  const sidebarLinks = [
    { text: "About Us", border: "#ccc", showIcon: false, path: "/about" },
    { text: "Meet the Board", border: "#ccc", showIcon: true, path: "/about/meet-the-board" },
    { text: "Work We Do", border: null, showIcon: true, path: "/about/work-we-do" },
    { text: "Education", border: null, showIcon: false, path: "/about/work-we-do/education" },
    { text: "Regulation", border: null, showIcon: false, path: "/about/work-we-do/regulation" },
    { text: "Membership Support", border: "#ccc", showIcon: false, path: "/about/work-we-do/membership-support" },
    { text: "Our Policies", border: "#ccc", showIcon: true, path: "/about/our-policies" },
    { text: "Become a Member", border: "#ccc", showIcon: true, path: "/about/become-member" },
    { text: "DLA Fellowship", border: null, showIcon: true, path: "/about/dla-fellowship" },
    { text: "DLA Fellowship Awards", border: null, showIcon: false, path: "/about/dla-fellowship/awards" },
    { text: "DLA Special Recognition Award", border: "#ccc", showIcon: false, path: "/about/dla-fellowship/special-recognition" },
  ];

  const handleSidebarClick = (path) => {
    navigate(path);
    if (windowWidth <= 1029) {
      setIsSidebarOpen(false);
    }
  };

  const toggleSidebar = () => {
    setIsSidebarOpen(!isSidebarOpen);
  };

  const handleBreadcrumbClick = (path) => {
    navigate(path);
  };

  const handleMemberClick = (memberId, memberRoute) => {
    if (memberRoute && memberRoute.trim() !== "") {
      navigate(memberRoute);
    } else {
      navigate(`/board-member/${memberId}`);
    }
  };

  const handleReadMore = (e, memberId, memberRoute) => {
    e.preventDefault();
    e.stopPropagation();
    if (memberRoute && memberRoute.trim() !== "") {
      navigate(memberRoute);
    } else {
      navigate(`/board-member/${memberId}`);
    }
  };

  const getImageUrl = (imageUrl) => {
    if (!imageUrl) return null;
    if (imageUrl.startsWith('http://') || imageUrl.startsWith('https://')) {
      return imageUrl;
    }
    if (imageUrl.startsWith('/media/')) {
      return `https://backend.dlapakistan.com${imageUrl}`;
    }
    if (imageUrl.startsWith('/')) {
      return `https://backend.dlapakistan.com${imageUrl}`;
    }
    return imageUrl;
  };

  const stripHtml = (html) => {
    if (!html) return '';
    const temp = document.createElement('div');
    temp.innerHTML = html;
    return temp.textContent || temp.innerText || '';
  };

  const mobileOverlayClass = isMobileView && isSidebarOpen ? "pt-0" : "";

  return (
    <div 
      className={`w-full min-h-screen ${mobileOverlayClass}`}
      ref={headerRef}
    >
      <div className="w-full px-4 mt-52 sm:px-6 md:px-8 lg:px-10 xl:px-12 mx-auto">
        
        <div className="flex flex-wrap items-center text-gray-500 mb-4 md:mb-6 text-sm md:text-base">
          <span 
            className="cursor-pointer hover:underline hover:text-[#70B444] transition-colors"
            onClick={() => handleBreadcrumbClick("/")}
          >
            Home
          </span> 
          <span className="mx-2">»</span>
          <span className="text-[#70B444] font-medium">
            Our Board Members
          </span>
        </div>

        <div className="flex flex-col lg:flex-row gap-6 lg:gap-8 relative pb-8 md:pb-12">
          
          {!isMobileView && (
            <div className="w-full lg:w-72 xl:w-80 shrink-0 bg-[#f7f9fc] p-4 md:p-5 rounded-lg shadow-sm h-fit sticky top-24">
              <div className="bg-[#70B444] text-white p-3 text-center mb-4">
                <h3 className="m-0 text-base lg:text-lg font-semibold">About The DLA</h3>
              </div>
              {sidebarLinks.map((item, index) => (
                <div
                  key={index}
                  onClick={() => handleSidebarClick(item.path)}
                  className={`flex items-center py-2.5 text-[#70B444] font-medium cursor-pointer transition-all hover:bg-[rgba(112,180,68,0.1)] px-2 text-sm lg:text-base ${
                    item.border ? "border-b border-gray-200" : ""
                  } ${location.pathname === item.path ? "bg-[rgba(112,180,68,0.1)] border-l-4 border-l-[#70B444]" : ""}`}
                >
                  {item.showIcon && <BiCheckbox className="mr-2 text-lg flex-shrink-0" />}
                  <span className="break-words">{item.text}</span>
                </div>
              ))}
            </div>
          )}

          <div className={`flex-1 ${isMobileView ? 'p-0' : 'p-2 md:p-4'} ${
            isMobileView && isSidebarOpen ? "opacity-30 pointer-events-none" : "opacity-100"
          }`}>
            
            <div className="flex justify-between items-center border-b-2 border-[#70B444] pb-3 md:pb-4 mb-4 md:mb-6">
              <h1 className="text-xl sm:text-2xl md:text-3xl lg:text-4xl font-bold text-[#70B444] leading-snug">
                {headerData.main_heading || "Meet the Board"}
              </h1>
              
              {isMobileView && (
                <button
                  onClick={toggleSidebar}
                  className="flex items-center gap-2 text-[#70B444] border border-[#70B444] rounded-lg px-3 py-2 hover:bg-[#70B444] hover:text-white transition-all text-sm font-semibold shrink-0"
                >
                  {isSidebarOpen ? <FaTimes /> : <FaBars />}
                  <span>{isSidebarOpen ? "Close" : "Menu"}</span>
                </button>
              )}
            </div>

            <div className="mb-8 md:mb-10 text-gray-700 space-y-4 md:space-y-6">
              <div className="leading-relaxed text-sm sm:text-base md:text-lg">
                {headerData.description ? (
                  <div dangerouslySetInnerHTML={{ __html: headerData.description }} />
                ) : (
                  <p>The directors of the DLA board are elected to their positions by the DLA members at the AGM. The President and Vice President of the DLA board are elected on a five-year term, the rest of the DLA board members are elected on a three-year term.</p>
                )}
              </div>
            </div>

            {loading && (
              <div className="flex justify-center py-8">
                <div className="animate-spin rounded-full h-10 w-10 border-t-2 border-b-2 border-[#70B444]"></div>
              </div>
            )}

            {/* Board Members Grid - Original larger card sizes, 4 cards per row */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 md:gap-7">
              {boardMembers.map((member) => {
                const imageUrl = getImageUrl(member.image_url) || `/dla${member.id}.png`;
                const plainDescription = stripHtml(member.description || '');
                
                return (
                  <div 
                    key={member.id}
                    className="bg-white rounded-xl overflow-hidden shadow-sm hover:shadow-lg transition-all cursor-pointer group flex flex-col"
                    onClick={() => handleMemberClick(member.id, member.route)}
                  >
                    {/* Image on top - full width, no borders, perfectly fitted */}
                    <div className="w-full bg-gray-100">
                      <img
                        src={imageUrl}
                        alt={member.name}
                        className="w-full h-auto object-contain group-hover:scale-105 transition-transform duration-300"
                        onError={(e) => {
                          e.target.src = `https://ui-avatars.com/api/?name=${encodeURIComponent(member.name)}&background=70B444&color=fff&size=400`;
                        }}
                      />
                    </div>
                    <div className="p-5 flex flex-col flex-1">
                      <h3 className="text-xl font-bold text-[#70B444] mb-2 group-hover:text-[#5d9a3a] transition-colors line-clamp-1">
                        {member.name}
                      </h3>
                      <p className="text-gray-700 font-medium text-base mb-3 line-clamp-2 min-h-[3rem]">
                        {member.position}
                      </p>
                      <p className="text-gray-500 text-sm line-clamp-2 mb-4 min-h-[2.5rem]">
                        {plainDescription || "No biography available."}
                      </p>
                      <button
                        onClick={(e) => handleReadMore(e, member.id, member.route)}
                        className="text-[#70B444] font-semibold hover:text-[#5d9a3a] transition-colors text-sm flex items-center gap-1 mt-auto"
                      >
                        Read More →
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>

            {!loading && boardMembers.length === 0 && (
              <div className="text-center py-12">
                <div className="text-gray-400 text-6xl mb-4">👥</div>
                <h3 className="text-xl font-semibold text-gray-600 mb-2">No Board Members</h3>
                <p className="text-gray-500">No board members found in the database.</p>
              </div>
            )}
          </div>

          {isMobileView && (
            <div
              className={`fixed top-0 right-0 h-full w-[85vw] max-w-sm bg-white z-[60] shadow-2xl transform transition-transform duration-300 ease-in-out ${
                isSidebarOpen ? "translate-x-0" : "translate-x-full"
              }`}
              style={{ top: `${headerHeight}px`, height: `calc(100vh - ${headerHeight}px)` }}
            >
              <div className="flex flex-col h-full">
                <div className="p-4 border-b flex justify-between items-center bg-[#70B444] text-white">
                  <span className="font-bold text-lg">Navigation</span>
                  <FaTimes 
                    className="cursor-pointer text-2xl" 
                    onClick={toggleSidebar} 
                  />
                </div>
                <div className="overflow-y-auto flex-1 p-2">
                  {sidebarLinks.map((item, index) => (
                    <div
                      key={index}
                      onClick={() => handleSidebarClick(item.path)}
                      className={`p-3 md:p-4 text-base border-b border-gray-100 flex items-center hover:bg-gray-50 ${
                        location.pathname === item.path 
                          ? "text-[#70B444] font-semibold bg-green-50 border-l-4 border-l-[#70B444]" 
                          : "text-gray-700"
                      }`}
                    >
                      {item.showIcon && <BiCheckbox className="mr-3 text-xl text-[#70B444] flex-shrink-0" />}
                      <span>{item.text}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {isMobileView && isSidebarOpen && (
            <div
              className="fixed inset-0 bg-black/50 z-[50] transition-opacity"
              style={{ top: `${headerHeight}px` }}
              onClick={toggleSidebar}
            />
          )}
        </div>
      </div>
    </div>
  );
}

export default MeetTheBoard;